import { Login as HaiLogin, Icon } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { isNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MakitoLogoFX from 'src/images/makito-fx-logo.svg';
import MakitoLogoX1 from 'src/images/makito-x1-logo.svg';
import MakitoLogoX4 from 'src/images/makito-x4-logo.svg';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import systemInfo, { fetchSystemInfo, setSystemInfoProperty } from 'src/js/data/systemInfo';
import { triggerEvent } from 'src/js/events';
import { useAuthenticated } from 'src/js/hook/use-authenticated';
import { useStores } from 'src/js/hook/use-stores';
import { mapSessionViewModel } from 'src/js/mapper/session-view-model-mapper';
import { NotificationVariant } from 'src/js/notification/notification';
import { clearAllPermanentNotifications, createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { BannerIfAny } from 'src/js/pages/login/banner-if-any';
import { fetchPresets } from 'src/js/pages/presets/preset-store';
import { defaultRoute } from 'src/js/route';
import { trunk } from 'src/js/service/persistence';
import { persistedStores } from 'src/js/store';
import { PRODUCT, constant } from 'src/js/constant';
import { SessionViewModel } from 'src/js/view-model/session-view-model';
import { LoginViewModel, loginSchema } from './login-view-model';
import { PasswordExpired } from './passwordExpired';
import whatsNewStore from 'src/js/store/whats-new-store';
import { updateWatchStore } from 'src/js/store/use-watch';
import accountModels from 'src/js/pages/security/accounts/account-store';
import streams from 'src/js/pages/streaming/stream/stream-store';
import { LoginResponse } from 'src/js/model/api/response/login-response';

interface Props {
  redirectToPath?: string;
}

/**
 *
 * @param reload default false, set true to trigger location.reload()
 * @param navigate required if reload is false
 */
//TODO: split in two functions instead of having parameters
export const handleLogout = (reload = false, navigate: Function) => {
  persistedStores.sessionStore.logout();
  trunk.persist();
  updateWatchStore(accountModels, []); // NKE-3107
  updateWatchStore(streams, []); // clear passphrases from memory
  reload ? location.reload() : navigate(defaultRoute);
};

const decideIfWeShowWhatsNewModal = () => {
  const version = systemInfo.version;
  if (!isNil(version)) {
    const trVersion = version.substring(0, version.indexOf('-'));
    const whatsNewHiddenVersion = localStorage.getItem(constant.lStorage.whatsNewHiddenVersion);
    // console.log(whatsNewHiddenVersion, trVersion, sessionStorage.ssoAccess);
    if (whatsNewHiddenVersion != trVersion && !sessionStorage.ssoAccess) {
      whatsNewStore.showWhatsNew = true;
    }
  }
};

export const clearLocalStorageAfterLogin = (username: string) => {
  localStorage.removeItem(`${constant.lStorage.streamingTab}:${username}`);
  localStorage.removeItem(`${constant.lStorage.settingsTab}:${username}`);
  localStorage.removeItem(`${constant.lStorage.securityTab}:${username}`);
  localStorage.removeItem(`${constant.lStorage.showUserAnalytics}`);
};

export const Login: React.FunctionComponent<Props> = ({ redirectToPath }) => {
  const { t } = useTranslation();
  const authenticated = useAuthenticated();
  const navigate = useNavigate();
  const { sessionStore } = useStores();
  const [isPasswordExpired, setPasswordExpired] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [loading, setLoading] = useState(true);
  const [sessionViewModel, setSessionViewModel] = useState<SessionViewModel>(undefined);
  const [periodicHuntForAuth, setPeriodicHuntForAuth] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>();

  // if we need to display hostname/fqdn
  // const [fqdn, setFqdn] = useState(systemInfo.fqdn);
  // useWatch(systemInfo, 'fqdn', setFqdn);

  useEffect(() => {
    clearAllPermanentNotifications();
  }, []);

  useEffect(() => {
    if (isNil(redirectToPath)) {
      navigate(defaultRoute);
    } else {
      navigate(redirectToPath, { replace: true });
    }

    // If version has changed, issue hard reload document.location.reload();
    if (systemInfo.upgradePending) {
      setSystemInfoProperty('upgradePending', false); // this is not really needed because the whole page will be refreshed, but for style.
      (location.reload as any)(true); // "true" for Firefox only. Ignored by other browsers https://developer.mozilla.org/en-US/docs/Web/API/Location/reload
    }

    if (!authenticated) {
      // fetch session if any. Needed for single sign-on (sso)
      apiClientWithoutHandler.rawController.get(APIPath.session).then((res) => {
        if (res.ok) {
          const response = res.data as LoginResponse;
          if (response.passwordExp == false) {
            clearLocalStorageAfterLogin(response.username);
            sessionStore.login(response.username, response.gid); // this triggers 'authenticated' to change
            triggerEvent('routes-refresh');
            trunk.persist();
          }
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [authenticated, history, redirectToPath]);

  // periodic hunt for back authenticated
  const loginCheckIntervalSec = 8;
  useEffect(() => {
    let endrun = false;
    let timer: any;
    const triggerRefresh = () => {
      apiClientWithoutHandler.rawController.get(APIPath.status.get).then(async (res: any) => {
        if (!res.ok && res.status === 401) {
          // unauthenticated
        } else if (res.ok && res.status === 200 && !endrun) {
          // we are possibly authenticated. Verify that the password is not expired
          const logged = await apiClientWithoutHandler.rawController.get(APIPath.session).then((res2) => {
            if ((res2.data as LoginResponse)?.passwordExp) {
              // ignore OK session because the password is expired
              return false;
            }
            // accepting session
            return true;
          });
          if (logged) {
            // back authenticated
            document.location.reload();
            return; // stop hunt
          }
        }

        if (periodicHuntForAuth) {
          timer = setTimeout(() => {
            triggerRefresh();
          }, loginCheckIntervalSec * 1000);
        }
      });
    };
    if (periodicHuntForAuth) {
      timer = setTimeout(() => {
        triggerRefresh();
      }, loginCheckIntervalSec * 1000);
    }

    return () => {
      // cleanup
      endrun = true;
      clearTimeout(timer);
    };
  }, [periodicHuntForAuth]);

  /* If we need remember me later on
  const [rememberMe, setRememberMe] = useLocalStorage(
    `${constant.lStorage.rememberMe}:${sessionStore.username}`,
    false,
  );
  const [userName, setUserName] = useLocalStorage(
    `${constant.lStorage.loginUsername}:${sessionStore.username}`,
    '',
  );
  const toggleRememberMe = (checked: boolean) => {
    setRememberMe(checked);
    if (!checked) {
      setUserName('');
    }
  };
  */

  const handleLogin = (username: string, password: string) => {
    setButtonState('pending');
    setPeriodicHuntForAuth(false);
    const viewModel: LoginViewModel = {
      username: username,
      password: password,
    };

    return apiClientWithoutHandler.rawController.post(APIPath.session, viewModel).then(async (res) => {
      if (res.ok) {
        const response: any = res.data;
        if (response) {
          const sessionViewModel = mapSessionViewModel(response.data);

          setButtonState(undefined);
          setErrorMessage(null);
          if (!isNil(sessionViewModel)) {
            // if (rememberMe) {
            //   setUserName(sessionViewModel.username);
            // }

            await fetchSystemInfo().then((licenseResponse) => {
              if (!licenseResponse.data.licensed) {
                createAndDispatchNotification(t('error.systemNotLicensed'), NotificationVariant.WARNING, t, {
                  autohide: false,
                });
                if (!sessionStore.isUser()) {
                  navigate('/settings/licensing');
                }
              } else {
                createAndDispatchNotification(response.message, NotificationVariant.REMINDER, t);
              }
              return licenseResponse;
            });

            if (sessionViewModel.passwordExp) {
              setSessionViewModel(sessionViewModel);
              setPasswordExpired(true);
              trunk.persist();
            } else {
              // Registration of login to the UI application, triggering page change
              clearLocalStorageAfterLogin(sessionViewModel.username);
              sessionStore.login(sessionViewModel.username, sessionViewModel.gid); // this triggers 'authenticated' to change
              triggerEvent('routes-refresh');
              decideIfWeShowWhatsNewModal(); // What's new modal
              trunk.persist();
            }

            // Guest role has no access to presets
            if (sessionViewModel.gid !== 513) {
              fetchPresets(t);
            }
          }
        }
        return res;
      } else {
        if (res.status === 401) {
          setErrorMessage(t('error.loginErrorIncorrect'));
        } else if (res.status === 503) {
          setErrorMessage(t('error.loginErrorTooMany'));
        } else {
          setErrorMessage(t('error.loginErrorUnknown'));
        }
        setButtonState(undefined);
        return res;
      }
    });
  };

  let logo = MakitoLogoX4;
  if (constant.product === PRODUCT.FX) {
    logo = MakitoLogoFX;
  } else if (constant.product === PRODUCT.X1) {
    logo = MakitoLogoX1;
  }

  if (authenticated) {
    return null;
  } else {
    return (
      <div className="login">
        {!loading && (
          <BannerIfAny>
            {isPasswordExpired && sessionViewModel ? (
              <PasswordExpired session={sessionViewModel} />
            ) : (
              <HaiLogin
                accentColor={constant.productColor}
                useFullPageContainer={true}
                appLogo={<Icon src={logo} style={{ height: '70px' }} />}
                errorMessages={{
                  general: errorMessage,
                  username: errorMessage && '\u00A0',
                }}
                onInputChange={(evt: any) => {
                  if (evt && evt.target && evt.target.value === '') {
                    //console.log('clearing');
                    setErrorMessage(null);
                  }
                }}
                capsLockMessage={t('login.capsLockOn')}
                /* No footer at the moment for "security" reason
                FIXME: if fqdn fits without ellipsis, display it
                formFooter={
                  <span className="login-footer-info" title={fqdn}>
                    {hostname}
                  </span>
                } */
                handleLogin={handleLogin}
                signInButton={{
                  caption: t('login.signin'),
                  state: buttonState,
                }}
                usernameLabel={t('security.accounts.form.userName')}
                usernamePlaceholder=""
                passwordLabel={t('security.accounts.form.password')}
                passwordPlaceholder=""
                validationSchema={loginSchema(t)}
              ></HaiLogin>
            )}
          </BannerIfAny>
        )}
      </div>
    );
  }
};
